import { sortBy, startCase } from 'lodash';
import { FilterContext } from 'api/automation-builder/types';
import { FieldSection, FieldTypeDisplay } from 'api/field-sections/types';
import type { Team, User as UserT } from 'api/models';
import { SprintPoints } from 'api/sprint-planning/types';
import type { Field, FieldOptionBase } from 'api/tasks';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';
import { unreachable } from 'utils/unreachable';

export enum SourceType {
  Task = 'task',
  Project = 'project',
  ProjectPhase = 'project-phase',
  Timesheet = 'timesheet',
  User = 'user',
  Phase = 'phase',
  Sprint = 'sprint',
  Company = 'company',
  Role = 'role',
  StarMeta = 'starmeta',
  ResourceRequest = 'resourcerequest',
  RequestApproval = 'requestapproval',
  Financial = 'financial',
  Formula = 'formula',
}

export enum FieldType {
  Boolean = 'Boolean',
  Number = 'Number',
  Text = 'Text',
  MultiLineText = 'MultiLineText',
  Date = 'Date',
  SingleSelect = 'SingleSelect',
  MultiSelect = 'MultiSelect',
  SingleUser = 'SingleUser',
  MultiUser = 'MultiUser',
  Assignee = 'Assignee',
  Estimate = 'Estimate',
  Note = 'Note',
}

export const FIELD_ID_ASSIGNEE = -127;

export const UserTypeFields = [
  FieldType.SingleUser,
  FieldType.MultiUser,
  FieldType.Assignee,
];

const calculatedFieldTypeMap = {
  currentPhases: FieldType.Text,
  actualStartDate: FieldType.Date,
  actualCompletedDate: FieldType.Date,
  customerCompanyName: FieldType.Text,
  completionPercentage: FieldType.Number,
  projectTrackedHours: FieldType.Number,
  totalBillableHours: FieldType.Number,
  overallRating: FieldType.Number,
  lastMilestoneRating: FieldType.Number,
  projectAge: FieldType.Number,
  plannedDuration: FieldType.Number,
  completedTasks: FieldType.Number,
  overdueTasks: FieldType.Number,
  blockedTasks: FieldType.Number,
  atRiskTasks: FieldType.Number,
  approvalPending: FieldType.Number,
};

// TODO: Get rid of quick-fix parse (with all caps shortcut)
export const filterableFieldType = (field: Field): FieldType => {
  if (field.fieldName === 'assignee') {
    return FieldType.Assignee;
  }

  if (field.fieldName === 'estimatedDueDate') {
    return FieldType.Estimate;
  }
  if (calculatedFieldTypeMap[field.fieldName]) {
    return calculatedFieldTypeMap[field.fieldName];
  }

  const type = field.optionsAvailable ? field.fieldType : field.fieldDataType;

  switch (type) {
    case 'BOOLEAN':
      return FieldType.Boolean;
    case 'NUMBER':
      return FieldType.Number;
    case 'TEXT':
      return FieldType.Text;
    case 'MULTI_LINE_TEXT':
      return FieldType.MultiLineText;
    case 'DATE':
      return FieldType.Date;
    case 'USER':
      return field.fieldType === 'SINGLE_SELECT'
        ? FieldType.SingleUser
        : FieldType.MultiUser;
    case 'SINGLE_SELECT':
      return FieldType.SingleSelect;
    case 'MULTI_SELECT':
      return field.fieldDataType === 'NOTE'
        ? FieldType.Note
        : FieldType.MultiSelect;
    case 'NOTE':
      return FieldType.Note;
    default:
      return unreachable(type as never);
  }
};

export interface FieldBase<T> {
  fieldId: number;
  fieldName: string;
  fieldLabel?: string;
  fieldIndex?: number;
  description?: string;
  fieldGroup?: string;
  private: boolean;
  value?: T;
  customField?: boolean;
  metaData?: {
    required?: boolean;
    relatedFieldId?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    options?: FieldOptionBase<number>[];
    roleIds?: number[];
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
    /**
     * This is just a FE property
     */
    filterContext?: FilterContext;
    pointType?: SprintPoints;
  };
  userCreated?: boolean;
  isEnabled?: boolean;
  isEditable?: boolean;
  fieldType?: string;
  label?: string;
  sourceType: SourceType;
  userGroupIds?: number[];
  objectType?: string; // TODO: fix circular dependency in typing
  objectSubType?: string;
  fieldTypeDisplay?: FieldTypeDisplay | null;
}

export interface BooleanField extends FieldBase<boolean> {
  type: FieldType.Boolean;
}

export interface NumberField extends FieldBase<number> {
  type: FieldType.Number;
  operation?: string;
  metaData?: {
    required: boolean;
    default?: number;
    range?: {
      min?: number;
      max?: number;
    };
    options?: FieldOptionBase<number>[];
    rangeAvailable: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  };
}

export interface EstimateField extends FieldBase<number> {
  type: FieldType.Estimate;
  metaData?: {
    required: boolean;
    default?: number;
    options?: FieldOptionBase<number>[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  };
}

export interface TextField extends FieldBase<string> {
  type: FieldType.Text;
}

export interface MultiLineTextField extends FieldBase<string> {
  type: FieldType.MultiLineText;
}

export interface DateField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Date;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    options?: FieldOptionBase<number>[];
    captureKeyIntervalForFirstActivityOnly?: boolean;
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowBaseOps?: boolean;
    /**
     * This is just a FE property
     */
    showAllOps?: boolean;
    /**
     * This is just a FE property
     */
    filterContext?: FilterContext;
    pointType?: SprintPoints;
  };
}

export interface RatingField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Number;
  fieldType: FieldTypes.Rating;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    ratingScale?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  };
}

export interface ProjectUsers {
  in_project_user_ids: number[];
  not_in_project_user_ids: number[];
}
export interface ProjectUsersByAccess {
  private: ProjectUsers;
  shared: ProjectUsers;
}

export interface UsersMetadata {
  by_user_id: {
    [userId: number]: UserT;
  };
  active_user_ids: number[];
  non_external_user_ids: number[];
  by_project_id: {
    [project_id: number]: ProjectUsersByAccess;
  };
}

export interface UserFieldMetadata {
  required?: boolean;
  users: UsersMetadata;
  options?: FieldOptionBase<number>[];
  allowAdminsAndOwnersToEdit?: boolean;
  captureKeyIntervalForFirstActivityOnly?: boolean;
  pointType?: SprintPoints;
}

export interface SingleUserField extends FieldBase<number> {
  type: FieldType.SingleUser;
  metaData: UserFieldMetadata;
}

export interface MultiUserField extends FieldBase<number[]> {
  type: FieldType.MultiUser;
  metaData: UserFieldMetadata;
}

export interface SingleSelectField extends FieldBase<number> {
  type: FieldType.SingleSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    default?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  };
}

export interface MultiSelectField extends FieldBase<number[]> {
  type: FieldType.MultiSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  };
}

export interface NoteField extends FieldBase<string> {
  type: FieldType.Note;
  metaData: {
    required?: boolean;
    tags: FieldOptionBase[];
    options?: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  };
}

export type UpdatableSquad = {
  users: Pick<UserT, 'userId'>[];
  teams: Pick<Team, 'teamId'>[];
};

export type UserId = ['user', number];
export const UserId = (id: number): UserId => ['user', id];

export type TeamId = ['team', number];
export const TeamId = (id: number): TeamId => ['team', id];

export type AssigneeValue = UserId | TeamId;
export const AssigneeValue = {
  toString: (value: AssigneeValue): string => value.join('_'),
  fromString: (value: string): AssigneeValue => {
    const [type, id] = value.split('_');

    return [type, parseInt(id, 10)] as AssigneeValue;
  },
  isSame: (a: AssigneeValue, b: AssigneeValue): boolean =>
    a[0] === b[0] && a[1] === b[1],
  toSquad: (values: AssigneeValue[]): UpdatableSquad => ({
    users: values
      .filter(([type]) => type === 'user')
      .map(([, userId]) => ({ userId })),
    teams: values
      .filter(([type]) => type === 'team')
      .map(([, teamId]) => ({ teamId })),
  }),
};

export interface AssigneeField extends FieldBase<AssigneeValue[]> {
  type: FieldType.Assignee;
  fieldGroup: string;
  metaData: UserFieldMetadata & {
    teams: Team[];
    projectPlaceholders?: { [id: number]: Team[] };
  };
}

export const AssigneeField = (
  users: UsersMetadata,
  teams: Team[],
): AssigneeField => ({
  fieldId: FIELD_ID_ASSIGNEE,
  private: false,
  type: FieldType.Assignee,
  customField: false,
  fieldName: 'assignee',
  metaData: { users, teams },
  fieldGroup: '_native',
  sourceType: SourceType.Task,
});

export type FilterableField =
  | BooleanField
  | NumberField
  | TextField
  | MultiLineTextField
  | DateField
  | SingleUserField
  | MultiUserField
  | SingleSelectField
  | MultiSelectField
  | AssigneeField
  | EstimateField
  | NoteField;

export type FieldLookupByName = Record<string, FilterableField>;
export type FieldLookupById = Record<number, FilterableField>;

export interface GroupedOptions<T> {
  id: string;
  label?: string;
  icon?: JSX.Element;
  fieldSections?: FieldSection[] | undefined;
  options: T[];
}
export type LabelledOptions<T> = Omit<
  GroupedOptions<T>,
  'id' | 'icon' | 'fieldSections'
> & {
  label?: JSX.Element | string;
};

export interface GroupedOptionsBySection<T, R>
  extends Omit<GroupedOptions<T>, 'options'> {
  options: R[];
}

export const groupedFieldsTransformerFunction = (
  fieldOption: GroupedOptions<FilterableField>,
): GroupedOptions<FilterableField> => ({
  ...fieldOption,
  label: fieldOption.label || startCase(fieldOption.id),
  options: sortBy(
    fieldOption.options.map((_option) => ({
      label: _option.fieldLabel,
      value: _option.fieldId as any,
      ..._option,
    })),
    ({ fieldLabel, fieldName }) => fieldLabel ?? startCase(fieldName),
  ),
});
